/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { ButtonContainer, screenSize } from 'styles/shared'
import { color } from 'styles/colors'

import ModalButton from 'components/Modal/Button'
import Checkbox from 'components/Checkbox'
import TextInput from 'components/TextInput'
import ErrorMessage from 'components/ErrorMessage'
import Spinner from 'components/Spinner'
import Textarea from 'components/Textarea'

import { scrubNull } from 'helpers/formatters'

import useDealer from 'hooks/useDealer'
import { useSessionContext } from 'hooks/SessionContext'
import { usePremiumFeaturesContext } from 'hooks/PremiumFeaturesContext'
import useEmailUnsubscribeStatus from 'hooks/useEmailUnsubscribeStatus'

import Api from 'services/api'
import { DeviceBase } from 'types/Device'
import { FormData } from './SystemReportsModal'

type Props = {
  selectEmailSystemReport: () => void
  resetAndCloseModal: () => void
  devices: Array<DeviceBase>
  errors?: { devices?: string }
  handleGenerateReport: (imageId: string) => void,
  handleFormData: (string, boolean) => void,
  formData: FormData,
  customerId: string,
  customerEmail: string,
  isDownloading: boolean
}

const DownloadSystemReport = ({ isDownloading, resetAndCloseModal, devices, customerId, customerEmail, selectEmailSystemReport, handleGenerateReport, handleFormData, formData, errors = {} }: Props) => {
  const { dealer: { imageUrl }, isDealerLoaded } = useDealer()
  const [reportImage, setReportImage] = useState<string | null>(null)
  const { session: { isImpersonating } } = useSessionContext()
  const { hasAccess, LockIcon, published } = usePremiumFeaturesContext()
  const { isUnsubscribed, areUnsubscribedLoaded } = useEmailUnsubscribeStatus(customerEmail, 'system_report')
  const isEmailButtonEnabled = customerEmail && hasAccess('email_system_reports') && !isImpersonating && !isUnsubscribed && areUnsubscribedLoaded && isDealerLoaded

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0]
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onloadend = async () => {
          const base64Data = (reader.result as string)
          const { imageId } = await Api.SystemReports.uploadReportImage(base64Data, customerId)
          setReportImage(base64Data)
          handleFormData('imageId', imageId)
        }
      }
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    setReportImage(scrubNull(imageUrl))
  }, [imageUrl])

  return (
    <>
      <S.FieldWrapper>
        <S.InputWrapper>
          <S.TextInput placeholder="First name" maxLength={20} value={formData.firstName} onChange={(event) => handleFormData('firstName', event.target.value)} />
          <S.TextInput placeholder="Last name" maxLength={20} value={formData.lastName} onChange={(event) => handleFormData('lastName', event.target.value)} />
        </S.InputWrapper>
      </S.FieldWrapper>

      {devices.length > 1 && (
        <S.FieldWrapper>
          <S.FormLabel>Create report for these systems</S.FormLabel>
          <S.ErrorMessage errorMessage={errors?.devices || ''} />
          <S.DeviceCheckboxes>
            {devices.map(({ deviceId }) => (
              <S.Checkbox
                key={deviceId}
                label={deviceId}
                checked={formData.selectedDeviceIds.includes(deviceId)}
                onChange={() => {
                  const updatedDeviceIds = formData.selectedDeviceIds.includes(deviceId) ? formData.selectedDeviceIds.filter(s => s !== deviceId) : [...formData.selectedDeviceIds, deviceId]
                  handleFormData('selectedDeviceIds', updatedDeviceIds)
                }}
              />))}
          </S.DeviceCheckboxes>
        </S.FieldWrapper>
      )}
      <S.FieldWrapper>
        <S.NotesInput
          value={formData.notes}
          onChange={event => { handleFormData('notes', event.target.value) }}
          label="Add notes to report(s)"
        />
      </S.FieldWrapper>
      {isDealerLoaded
        ? formData.useImageOnFile &&
        <S.ImageContainer>
          { reportImage && <S.Image id="image" alt="dealer logo" src={reportImage} />}
          <input
            type="file"
            onChange={handleFileChange}
            className="hidden"
            id="file-upload"
            accept="image/jpeg, image/png, image/bmp, image/gif"
            data-testid="upload-report-image"
          />
          <label htmlFor="file-upload"> {reportImage ? 'Use a different image for report(s)' : 'Upload dealer logo for report(s)'} </label>
        </S.ImageContainer>
        : <Spinner variant="page" />
      }
      <S.FieldWrapper>
        <S.FormLabel>Include in the report(s):</S.FormLabel>
        <S.Checkbox
          label={`Dealer logo${reportImage ? '' : ' (No logo on account)'}`}
          checked={formData.useImageOnFile && reportImage !== null}
          onChange={() => handleFormData('useImageOnFile', !formData.useImageOnFile)}
          disabled={!reportImage}
        />
        <S.Checkbox
          label="Service Contract status"
          checked={formData.useServiceContractStatus}
          onChange={() => { handleFormData('useServiceContractStatus', !formData.useServiceContractStatus) }}
        />
        <S.Checkbox
          label="Save to Diagnostics account"
          checked={formData.useSaveReportStatus}
          disabled={isImpersonating}
          onChange={() => { handleFormData('useSaveReportStatus', !formData.useSaveReportStatus) }}
        />
      </S.FieldWrapper>

      <S.ButtonContainer>
        <ModalButton onPress={resetAndCloseModal} decoration="textOnly" text="Cancel" />
        <ModalButton showSpinner={isDownloading} onPress={handleGenerateReport} decoration="secondary" text="Download Report" enabled={isDealerLoaded} />
        {published('email_system_reports') &&
          <S.EmailButtonWrapper data-testid="email-report-btn-wrapper">
            <LockIcon featureCode="email_system_reports" featureComponentType={'button'} />
            <ModalButton
              onPress={selectEmailSystemReport}
              decoration="positive"
              text="Email Report"
              enabled={isEmailButtonEnabled}
            />
          </S.EmailButtonWrapper>
        }
      </S.ButtonContainer>
      {isUnsubscribed && <S.Note>*Customer has opted out of receiving system report emails.</S.Note>}
      {!customerEmail && <S.Note>*No Customer email on file.</S.Note>}
    </>
  )
}

export default DownloadSystemReport

const S = {
  FieldWrapper: styled.div`
    > span{
      margin-bottom: 4px;
      display: block;
    }
    margin-bottom: 10px;
  `,
  InputWrapper: styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  `,
  TextInput: styled(TextInput)`
    width: calc(50% - 5px);
    &:focus{
      border: 1px solid ${color('primaryButton')};
      box-shadow: none;
    }
  `,
  DeviceCheckboxes: styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    @media ${screenSize.small} {
      grid-template-columns: 1fr 1fr;
    }
  `,
  Checkbox: styled(Checkbox)`
    padding: 4px 0px;
    border-width: 2px;
    margin-bottom: 4px;
    > label{
      ${({ disabled }) => !disabled && `color: ${color('checkboxText')};`}
    }
  `,
  ErrorMessage: styled(ErrorMessage)`
    margin-top: -30px;
  `,
  EmailButtonWrapper: styled.span`
    position: relative;
  `,
  NotesInput: styled(Textarea)`
    display: flex;
    flex-direction: column;
    > label{
      color: ${color('primaryText')};
    }
  `,
  ImageContainer: styled.div`
    height: 180px;
    width: 220px;
    margin: 0 auto;
    text-align: center;
    & input{
      visibility: hidden;
      width: 100%;
    }
    label{
      cursor: pointer;
      color: ${color('primaryButton')};
      text-decoration: underline;
      font-size: 12px;
      font-weight: 600;
      position: relative;
      top: -20px;
      padding: 4px 0px;
    }
  `,
  Image: styled.img`
    border: 1px solid ${color('border')};
    width: 100%;
    height: 120px;
    object-fit: contain;
  `,
  OkButtonLink: styled.a`
    margin-left: 10px;
    display: flex;
  `,
  ButtonContainer: styled(ButtonContainer)`
    border: none;
    align-items: center;
    gap: 5px;
  `,
  FormLabel: styled.label`
    color: ${color('primaryText')};
  `,
  Note: styled.label`
  margin-top: 6px;
  display: block;
  text-align:right`
}
